import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Star, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-white/20 py-4">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
        style={{ fontFamily: "'Lora', Georgia, serif" }}
      >
        <span className="font-semibold text-white">{title}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-white" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white" />
        )}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 mt-4' : 'max-h-0'}`}>
        {children}
      </div>
    </div>
  );
};

export const Footer = () => {
  const [recensioni, setRecensioni] = useState([]);
  const [currentReview, setCurrentReview] = useState(0);

  useEffect(() => {
    loadRecensioni();
  }, []);

  useEffect(() => {
    if (recensioni.length === 0) return;
    const interval = setInterval(() => {
      setCurrentReview(prev => (prev + 1) % recensioni.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [recensioni.length]);

  const loadRecensioni = async () => {
    try {
      const response = await axios.get(`${API}/recensioni`);
      setRecensioni(response.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  return (
    <footer className="py-16 relative overflow-hidden" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}}>
      <div className="absolute inset-0 opacity-25" style={{backgroundImage: "url('/bg-blue-4.jpg')", backgroundSize: 'cover', backgroundPosition: 'center'}} />
      {/* Mini Reviews Section */}
      {recensioni.length > 0 && (
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 pb-8" style={{borderBottom: '1px solid rgba(255,255,255,0.2)'}}>
          <div className="text-center">
            <div className="flex justify-center gap-1 mb-2">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-4 h-4 fill-current" style={{color: '#D4AF37'}} />
              ))}
            </div>
            <p className="text-sm italic mb-1 text-white/80" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              &quot;{recensioni[currentReview]?.testo?.substring(0, 100)}...&quot;
            </p>
            <p className="text-xs text-white" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              — {recensioni[currentReview]?.nome}
            </p>
          </div>
        </div>
      )}

      {/* PRIME VISION Header - V Logo */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="text-center py-8 border-b border-white/20">
          <img 
            src="/logo-v-footer.png" 
            alt="PRIME VISION" 
            className="h-32 md:h-40 lg:h-48 w-auto object-contain mx-auto"
          />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: Collapsible Sections */}
        <div className="md:hidden">
          <CollapsibleSection title="Collegamenti">
            <ul className="space-y-3 text-sm" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              <li><Link to="/" className="text-white/80 hover:text-white hover:underline">Home</Link></li>
              <li><Link to="/chi-siamo" className="text-white/80 hover:text-white hover:underline">Chi Siamo</Link></li>
              <li><Link to="/equipe" className="text-white/80 hover:text-white hover:underline">Équipe</Link></li>
              <li><Link to="/tests" className="text-white/80 hover:text-white hover:underline">Test Visivi</Link></li>
              <li><Link to="/booking" className="text-white/80 hover:text-white hover:underline">Prenota Visita</Link></li>
              <li><Link to="/abbonamenti" className="text-white/80 hover:text-white hover:underline">Abbonamenti</Link></li>
              <li><Link to="/news" className="text-white/80 hover:text-white hover:underline">News</Link></li>
              <li><Link to="/lavora-con-noi" className="text-white/80 hover:text-white hover:underline">Lavora con Noi</Link></li>
            </ul>
          </CollapsibleSection>

          <CollapsibleSection title="Legale">
            <ul className="space-y-3 text-sm" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              <li><Link to="/privacy" className="text-white/80 hover:text-white hover:underline">Privacy Policy</Link></li>
              <li><Link to="/privacy#cookie-policy" className="text-white/80 hover:text-white hover:underline">Cookie Policy</Link></li>
              <li>
                <button 
                  onClick={() => {
                    localStorage.removeItem('cookieConsent');
                    localStorage.removeItem('cookiePreferences');
                    window.location.reload();
                  }}
                  className="text-white/80 hover:text-white hover:underline text-left"
                >
                  Gestisci Cookie
                </button>
              </li>
            </ul>
          </CollapsibleSection>

          <CollapsibleSection title="Specializzazioni">
            <ul className="text-sm grid grid-cols-2 gap-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              <li><Link to="/specialization/refractive_errors" className="text-white/70 hover:text-white hover:underline">Vizi Refrattivi</Link></li>
              <li><Link to="/specialization/strabismus" className="text-white/70 hover:text-white hover:underline">Strabismo</Link></li>
              <li><Link to="/specialization/glaucoma" className="text-white/70 hover:text-white hover:underline">Glaucoma</Link></li>
              <li><Link to="/specialization/cataract" className="text-white/70 hover:text-white hover:underline">Cataratta</Link></li>
              <li><Link to="/specialization/maculopathy" className="text-white/70 hover:text-white hover:underline">Maculopatia</Link></li>
              <li><Link to="/specialization/dry_eye" className="text-white/70 hover:text-white hover:underline">Occhio Secco</Link></li>
              <li><Link to="/specialization/keratoconus" className="text-white/70 hover:text-white hover:underline">Cheratocono</Link></li>
              <li><Link to="/specialization/refractive_surgery" className="text-white/70 hover:text-white hover:underline">Chirurgia Refrattiva</Link></li>
              <li><Link to="/specialization/chalazion" className="text-white/70 hover:text-white hover:underline">Calazio</Link></li>
              <li><Link to="/pterigio" className="text-white/70 hover:text-white hover:underline">Pterigio</Link></li>
            </ul>
          </CollapsibleSection>

          <CollapsibleSection title="Contatti">
            <div className="space-y-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              {/* Sede Milano */}
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-sm font-semibold text-orange-300 mb-2">Sede Milano</p>
                <ul className="space-y-1.5 text-sm">
                  <li className="flex items-center space-x-2">
                    <MapPin className="w-3.5 h-3.5 text-white/70 shrink-0" />
                    <span className="text-white/80">Via Santa Sofia 10, 20122 Milano</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Phone className="w-3.5 h-3.5 text-white/70 shrink-0" />
                    <span className="text-white/80">+39 02 9876543</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Clock className="w-3.5 h-3.5 text-white/70 shrink-0" />
                    <span className="text-white/80">Lun-Ven 9:00-18:00</span>
                  </li>
                </ul>
              </div>
              {/* Sede Napoli */}
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-sm font-semibold text-sky-300 mb-2">Sede Napoli</p>
                <ul className="space-y-1.5 text-sm">
                  <li className="flex items-center space-x-2">
                    <MapPin className="w-3.5 h-3.5 text-white/70 shrink-0" />
                    <span className="text-white/80">Via Posillipo 168, Napoli (c/o Clinica Posillipo)</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Phone className="w-3.5 h-3.5 text-white/70 shrink-0" />
                    <span className="text-white/80">+39 081 1234567</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Clock className="w-3.5 h-3.5 text-white/70 shrink-0" />
                    <span className="text-white/80">Lun-Ven 9:00-18:00</span>
                  </li>
                </ul>
              </div>
              {/* Email / PEC */}
              <div className="text-sm space-y-1">
                <p className="flex items-center space-x-2">
                  <Mail className="w-3.5 h-3.5 text-white/70 shrink-0" />
                  <span className="text-white/80">info@primevision.it</span>
                </p>
                <p className="text-xs text-white/60">PEC: primevision@pec.it</p>
              </div>
            </div>
            <div className="flex space-x-4 mt-4">
              <a href="https://www.facebook.com/profile.php?id=61586161545013" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Facebook className="w-5 h-5 cursor-pointer text-white hover:opacity-80" />
              </a>
              <a href="https://www.instagram.com/primevision.it" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram className="w-5 h-5 cursor-pointer text-white hover:opacity-80" />
              </a>
              <a href="https://www.linkedin.com/company/primevision" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5 cursor-pointer text-white hover:opacity-80" />
              </a>
              <a href="https://www.tiktok.com/@primevision.it" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg className="w-5 h-5 cursor-pointer text-white hover:opacity-80" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </CollapsibleSection>
        </div>

        {/* Desktop: Grid Layout */}
        <div className="hidden md:grid grid-cols-4 gap-8">
          {/* Brand */}
          <div style={{ fontFamily: "'Lora', Georgia, serif" }}>
            <p className="text-sm text-white/80">
              Cura d'eccellenza per la vostra vista. Professionali, innovativi, dedicati alla prevenzione.
            </p>
            <div className="flex space-x-4 mt-6">
              <a href="https://www.facebook.com/profile.php?id=61586161545013" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Facebook className="w-5 h-5 cursor-pointer text-white hover:opacity-80" />
              </a>
              <a href="https://www.instagram.com/primevision.it" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram className="w-5 h-5 cursor-pointer text-white hover:opacity-80" />
              </a>
              <a href="https://www.linkedin.com/company/primevision" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5 cursor-pointer text-white hover:opacity-80" />
              </a>
              <a href="https://www.tiktok.com/@primevision.it" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg className="w-5 h-5 cursor-pointer text-white hover:opacity-80" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Collegamenti */}
          <div style={{ fontFamily: "'Lora', Georgia, serif" }}>
            <h4 className="font-semibold mb-4 text-white">Collegamenti</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-white/80 hover:text-white hover:underline">Home</Link></li>
              <li><Link to="/chi-siamo" className="text-white/80 hover:text-white hover:underline">Chi Siamo</Link></li>
              <li><Link to="/equipe" className="text-white/80 hover:text-white hover:underline">Équipe</Link></li>
              <li><Link to="/tests" className="text-white/80 hover:text-white hover:underline">Test Visivi</Link></li>
              <li><Link to="/booking" className="text-white/80 hover:text-white hover:underline">Prenota Visita</Link></li>
              <li><Link to="/abbonamenti" className="text-white/80 hover:text-white hover:underline">Abbonamenti</Link></li>
              <li><Link to="/news" className="text-white/80 hover:text-white hover:underline">News</Link></li>
            </ul>
          </div>

          {/* Legale + Specializzazioni */}
          <div style={{ fontFamily: "'Lora', Georgia, serif" }}>
            <h4 className="font-semibold mb-4 text-white">Legale</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="text-white/80 hover:text-white hover:underline">Privacy Policy</Link></li>
              <li><Link to="/privacy#cookie-policy" className="text-white/80 hover:text-white hover:underline">Cookie Policy</Link></li>
              <li>
                <button 
                  onClick={() => {
                    localStorage.removeItem('cookieConsent');
                    localStorage.removeItem('cookiePreferences');
                    window.location.reload();
                  }}
                  className="text-white/80 hover:text-white hover:underline text-left"
                >
                  Gestisci Cookie
                </button>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-6 text-white">Specializzazioni</h4>
            <ul className="space-y-1 text-xs grid grid-cols-2 gap-x-2">
              <li><Link to="/specialization/refractive_errors" className="text-white/70 hover:text-white hover:underline">Vizi Refrattivi</Link></li>
              <li><Link to="/specialization/strabismus" className="text-white/70 hover:text-white hover:underline">Strabismo</Link></li>
              <li><Link to="/specialization/glaucoma" className="text-white/70 hover:text-white hover:underline">Glaucoma</Link></li>
              <li><Link to="/specialization/cataract" className="text-white/70 hover:text-white hover:underline">Cataratta</Link></li>
              <li><Link to="/specialization/maculopathy" className="text-white/70 hover:text-white hover:underline">Maculopatia</Link></li>
              <li><Link to="/specialization/dry_eye" className="text-white/70 hover:text-white hover:underline">Occhio Secco</Link></li>
              <li><Link to="/specialization/keratoconus" className="text-white/70 hover:text-white hover:underline">Cheratocono</Link></li>
              <li><Link to="/specialization/refractive_surgery" className="text-white/70 hover:text-white hover:underline">Chirurgia Refrattiva</Link></li>
              <li><Link to="/specialization/chalazion" className="text-white/70 hover:text-white hover:underline">Calazio</Link></li>
              <li><Link to="/pterigio" className="text-white/70 hover:text-white hover:underline">Pterigio</Link></li>
            </ul>
          </div>

          {/* Contatti - Due sedi separate */}
          <div style={{ fontFamily: "'Lora', Georgia, serif" }}>
            <h4 className="font-semibold mb-4 text-white">Le Nostre Sedi</h4>
            {/* Milano */}
            <div className="bg-white/10 rounded-lg p-3 mb-3">
              <p className="text-sm font-semibold text-orange-300 mb-1.5">Milano</p>
              <ul className="space-y-1 text-xs">
                <li className="flex items-start space-x-1.5">
                  <MapPin className="w-3 h-3 text-white/70 mt-0.5 shrink-0" />
                  <span className="text-white/80">Via Santa Sofia 10, 20122</span>
                </li>
                <li className="flex items-center space-x-1.5">
                  <Phone className="w-3 h-3 text-white/70 shrink-0" />
                  <span className="text-white/80">+39 02 9876543</span>
                </li>
                <li className="flex items-center space-x-1.5">
                  <Clock className="w-3 h-3 text-white/70 shrink-0" />
                  <span className="text-white/80">Lun-Ven 9:00-18:00</span>
                </li>
              </ul>
            </div>
            {/* Napoli */}
            <div className="bg-white/10 rounded-lg p-3 mb-3">
              <p className="text-sm font-semibold text-sky-300 mb-1.5">Napoli</p>
              <ul className="space-y-1 text-xs">
                <li className="flex items-start space-x-1.5">
                  <MapPin className="w-3 h-3 text-white/70 mt-0.5 shrink-0" />
                  <span className="text-white/80">Via Posillipo 168 (c/o Clinica Posillipo)</span>
                </li>
                <li className="flex items-center space-x-1.5">
                  <Phone className="w-3 h-3 text-white/70 shrink-0" />
                  <span className="text-white/80">+39 081 1234567</span>
                </li>
                <li className="flex items-center space-x-1.5">
                  <Clock className="w-3 h-3 text-white/70 shrink-0" />
                  <span className="text-white/80">Lun-Ven 9:00-18:00</span>
                </li>
              </ul>
            </div>
            <p className="text-xs text-white/70 flex items-center gap-1.5">
              <Mail className="w-3 h-3 shrink-0" /> info@primevision.it
            </p>
            <p className="text-xs text-white/50 mt-0.5">PEC: primevision@pec.it</p>
            <Link 
              to="/contatti"
              className="text-xs mt-3 inline-flex items-center gap-1 text-white hover:underline font-semibold"
            >
              <MapPin className="w-3 h-3" />
              Tutte le sedi e indicazioni
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/20">
          <div className="text-center" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            <p className="text-sm text-white/80">
              © 2026 PRIME VISION - Tutti i diritti riservati
            </p>
            <p className="text-xs mt-1 text-white/60">
              Centro Oculistico - Milano | Napoli
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
